import React, { useState } from "react";
import { logError } from "@/lib/logError";
import axios from "axios";
import { useI18n, NLink } from "@/i18n/I18nProvider";
import { ArrowRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OFFICES = [
  { city: "Dover, Delaware", country: "USA" },
  { city: "Bern", country: "Switzerland" },
  { city: "Lisbon", country: "Portugal" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterForm = () => {
  const { t, lang } = useI18n();
  const f = t.footer;
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error | invalid

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setState("invalid");
      return;
    }
    setState("submitting");
    try {
      await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        language: lang,
        source: "footer",
      });
      setState("success");
      setEmail("");
    } catch (err) {
      logError("Footer.newsletter", err);
      setState("error");
    }
  };

  return (
    <div className="mt-10" data-testid="newsletter-block">
      <div className="overline text-white/40">{f.newsletterTitle}</div>
      <p className="mt-3 text-white/60 text-[13px] leading-relaxed max-w-sm">
        {f.newsletterKicker}
      </p>
      {state === "success" ? (
        <div
          data-testid="newsletter-success"
          className="mt-5 flex items-center gap-3 text-[#C4A46A] text-[13px] tracking-[0.14em] uppercase font-bold"
        >
          <Check size={16} /> {f.newsletterSuccess}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-5 flex items-stretch max-w-sm" data-testid="newsletter-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (state !== "idle") setState("idle");
            }}
            placeholder={f.newsletterPlaceholder}
            aria-label={f.newsletterPlaceholder}
            data-testid="newsletter-email"
            className="flex-1 bg-transparent border border-white/20 border-r-0 px-4 py-3 text-white text-[13px] placeholder:text-white/40 focus:border-[#C4A46A] focus:outline-none transition-colors"
          />
          <button
            type="submit"
            disabled={state === "submitting"}
            data-testid="newsletter-submit"
            className="px-5 bg-[#C4A46A] text-[#0F1B2D] text-[11px] tracking-[0.24em] uppercase font-bold hover:bg-[#A38755] transition-colors disabled:opacity-70 flex items-center gap-2"
          >
            {f.newsletterCta} <ArrowRight size={14} />
          </button>
        </form>
      )}
      {state === "invalid" && (
        <p className="mt-3 text-[12px] text-[#D96A6A]" data-testid="newsletter-error">
          {f.newsletterInvalid}
        </p>
      )}
      {state === "error" && (
        <p className="mt-3 text-[12px] text-[#D96A6A]" data-testid="newsletter-error">
          {f.newsletterError}
        </p>
      )}
    </div>
  );
};

const FooterColumn = ({ label, children }) => (
  <div>
    <div className="overline text-white/40">{label}</div>
    <ul className="mt-6 space-y-3 text-[14px] text-white/80">{children}</ul>
  </div>
);

const FooterLink = ({ to, label, testId }) => (
  <li>
    <NLink to={to} data-testid={testId} className="hover:text-[#C4A46A] transition-colors">
      {label}
    </NLink>
  </li>
);

const Footer = () => {
  const { t } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-[#0F1B2D] text-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand + Newsletter + Offices */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-4">
            <img
              src="/nc-logo.png"
              alt="Northcrest"
              width={56}
              height={56}
              className="block object-contain"
            />
            <div className="leading-[1.05]">
              <div className="font-display text-white tracking-[0.34em] text-[16px]">NORTHCREST</div>
              <div className="font-display text-[#C4A46A] tracking-[0.5em] text-[10px]">ATHLETICS</div>
            </div>
          </div>
          <p className="mt-8 max-w-md text-white/70 leading-relaxed text-[15px] font-display">
            {t.common.tagline.split(". ").map((line, i, arr) => (
              <span key={line} className="block">
                {line}{i < arr.length - 1 ? "." : ""}
              </span>
            ))}
          </p>
          <p className="mt-2 text-[#C4A46A] font-display tracking-[0.18em] text-[13px]">
            {t.common.taglineSecondary}
          </p>

          <NewsletterForm />

          <div className="mt-10">
            <div className="overline text-white/40">{t.footer.offices}</div>
            <ul className="mt-4 space-y-2 text-[13px] text-white/70">
              {OFFICES.map((o) => (
                <li key={o.city}>
                  <span className="text-white/85">{o.city}</span>
                  <span className="text-white/40"> — {o.country}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Programs */}
        <div className="md:col-span-2">
          <FooterColumn label={t.footer.programs}>
            <FooterLink to="/soccer-camps" label={t.nav.camps} testId="footer-camps" />
            <FooterLink to="/coach-education" label={t.nav.coachEd} testId="footer-coach-ed" />
          </FooterColumn>
        </div>

        {/* Discover */}
        <div className="md:col-span-3">
          <FooterColumn label={t.footer.discover}>
            <FooterLink to="/universities" label={t.nav.universities} testId="footer-universities" />
            <FooterLink to="/sports" label={t.nav.sports} testId="footer-sports" />
            <FooterLink to="/success-stories" label={t.nav.successStories} testId="footer-success-stories" />
            <FooterLink to="/resources" label={t.nav.resources} testId="footer-resources" />
          </FooterColumn>
        </div>

        {/* Company */}
        <div className="md:col-span-3">
          <FooterColumn label={t.footer.company}>
            <FooterLink to="/about" label={t.nav.about} testId="footer-about" />
            <FooterLink to="/partners" label={t.nav.partners} testId="footer-partners" />
            <FooterLink to="/contact" label={t.nav.contact} testId="footer-contact" />
            <FooterLink to="/legal" label={t.nav.legal} testId="footer-legal-menu" />
          </FooterColumn>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-white/50">
          <div>© {year} Northcrest Athletics. {t.footer.rights}</div>
          <div className="flex items-center gap-6">
            <NLink to="/legal" data-testid="footer-legal" className="hover:text-white">
              {t.nav.legal}
            </NLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
